import React, { useState, useEffect } from "react";
import {
  Card,
  TextInput,
  Button,
  Stack,
  Title,
  Text,
  Radio,
  Group,
  LoadingOverlay,
  Divider,
  Select,
  Flex,
  ActionIcon,
  Code,
  Box,
} from "@mantine/core";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { OrganizationSelect } from "@components/Organization";
import { LocationSelect } from "@components/Location";
import { IconX } from "@tabler/icons-react";
import { IconCircleCheck, IconCircleX } from "@tabler/icons-react";
import usStates from "@lib/usStates";

export default function LoyaltyMembershipLookup({
  userId,
  identOnly = false,
  title = "Loyalty Membership Lookup",
}) {
  const { t } = useTranslation();
  const [lookupType, setLookupType] = useState("manual");
  const [formData, setFormData] = useState({
    user_id: "",
    first_name: "",
    last_name: "",
    email: "",
    mobile_phone: "",
    city: "",
    state_province: "",
    organization_id: "",
    location_id: "",
    loyalty_membership_ident: "",
  });
  const [results, setResults] = useState(null);
  const showLookupType = userId ? false : true;
  const [showNoResultsMessage, setShowNoResultsMessage] = useState(false);

  useEffect(() => {
    setResults(null);
  }, [lookupType]);

  useEffect(() => {
    if (userId) {
      setFormData({
        ...formData,
        user_id: userId,
      });
    }
  }, [userId]);

  const mutation = useMutation({
    mutationFn: async (data) => {
      const requestData = {
        variety: 1,
        ...data,
      };

      setShowNoResultsMessage(false);
      setResults(null);

      // Remove empty fields
      Object.keys(requestData).forEach((key) => {
        if (requestData[key] === "" || requestData[key] === null) {
          delete requestData[key];
        }
      });

      const url = identOnly
        ? "/loyalty-memberships/ident-lookup/"
        : "/loyalty-memberships/admin-third-party-lookup/";

      return axios
        .post(url, requestData)
        .then(({ data }) => {
          if (identOnly) {
            setResults(data.response);
            if (data.response.length === 0) {
              setShowNoResultsMessage(true);
            } else {
              setShowNoResultsMessage(false);
            }
          } else {
            setResults(
              data.response[0]
                ? {
                    ...data.response[0],
                  }
                : []
            );
          }
          return data.response;
        })
        .catch((err) => {
          toast.error(err);
          throw err;
        });
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    let submitData = {};

    if (lookupType === "user_id") {
      if (
        !formData.user_id ||
        (!formData.organization_id && !formData.location_id)
      ) {
        toast.error("Please enter a user ID and organization/location");
        return;
      }
      submitData = {
        user_id: formData.user_id,
        organization_id: formData.organization_id,
        location_id: formData.location_id,
      };
    } else if (lookupType === "manual") {
      // If no loyalty_membership_ident, require both first_name and last_name
      if (!formData.loyalty_membership_ident) {
        // if (!formData.first_name || !formData.last_name || !formData.email) {
        //   toast.error(
        //     "If no ident is provided, both first name, last name, and email are required"
        //   );
        //   return;
        // }
      }
      if (identOnly && !formData.loyalty_membership_ident) {
        toast.error("Please enter an ident");
        return;
      }
      submitData = {
        first_name: formData.first_name || undefined,
        last_name: formData.last_name || undefined,
        email: formData.email || undefined,
        mobile_phone: formData.mobile_phone || undefined,
        // city: formData.city || undefined,
        // state_province: formData.state_province || undefined,
        loyalty_membership_ident:
          formData.loyalty_membership_ident || undefined,
      };
      Object.keys(submitData).forEach((key) => {
        if (typeof submitData[key] === "string") {
          submitData[key] = submitData[key].trim();
        }
      });
    } else {
      toast.error("Please select a lookup type");
      return;
    }

    mutation.mutate(submitData);
  };

  const handleInputChange = (field, value) => {
    setShowNoResultsMessage(false);
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const resetForm = () => {
    setFormData({
      user_id: "",
      first_name: "",
      last_name: "",
      email: "",
      mobile_phone: "",
      city: "",
      state_province: "",
      organization_id: "",
      location_id: "",
      loyalty_membership_ident: "",
    });
    setResults(null);
    setShowNoResultsMessage(false);
  };

  const getDisplayContacts = () => {
    if (identOnly) {
      return results;
    }
    if (results) {
      return results.contacts || results.members || [];
    }

    return [];
  };

  const displayContacts = getDisplayContacts();

  return (
    <div>
      <Card style={{ position: "relative" }}>
        <Flex mb="md" gap="xs" align="center">
          <Title order={3}>{title}</Title>
          <Button
            variant="light"
            size="xs"
            color="gray"
            radius="lg"
            onClick={resetForm}
          >
            {t("common:reset")}
          </Button>
        </Flex>
        <LoadingOverlay visible={mutation.isPending} />
        <form onSubmit={handleSubmit}>
          <Stack gap="md">
            {/* {showLookupType && (
              <Radio.Group
                value={lookupType}
                onChange={setLookupType}
                label="Lookup Type"
              >
                <Group mt="xs">
                  <Radio value="user_id" label="User ID" />
                  <Radio value="manual" label="Manual Entry" />
                </Group>
              </Radio.Group>
            )} */}
            {lookupType === "user_id" && (
              <>
                <Stack gap="sm">
                  {!userId && (
                    <TextInput
                      label="User ID"
                      placeholder="Enter user ID"
                      value={formData.user_id}
                      onChange={(e) =>
                        handleInputChange("user_id", e.target.value)
                      }
                      required
                    />
                  )}
                  <OrganizationSelect
                    label={t("mixer:organization_singular")}
                    value={formData.organization_id}
                    onChange={(e) => handleInputChange("organization_id", e)}
                    disabled={formData.location_id ? true : false}
                  />
                  <LocationSelect
                    label={t("mixer:location_singular")}
                    value={formData.location_id}
                    onChange={(e) => handleInputChange("location_id", e)}
                    disabled={formData.organization_id ? true : false}
                  />
                </Stack>
              </>
            )}
            {lookupType === "manual" && (
              <>
                <Stack gap="sm">
                  <TextInput
                    label="Ident"
                    placeholder="Enter ident"
                    value={formData.loyalty_membership_ident}
                    onChange={(e) =>
                      handleInputChange(
                        "loyalty_membership_ident",
                        e.target.value
                      )
                    }
                  />
                  {!identOnly && (
                    <>
                      <TextInput
                        label={t("common:first_name")}
                        placeholder="Enter first name"
                        value={formData.first_name}
                        onChange={(e) =>
                          handleInputChange("first_name", e.target.value)
                        }
                      />
                      <TextInput
                        label={t("common:last_name")}
                        placeholder="Enter last name"
                        value={formData.last_name}
                        onChange={(e) =>
                          handleInputChange("last_name", e.target.value)
                        }
                      />
                      <TextInput
                        label={t("common:email")}
                        placeholder="Enter email"
                        type="email"
                        value={formData.email}
                        onChange={(e) =>
                          handleInputChange("email", e.target.value)
                        }
                      />
                      <TextInput
                        label={t("common:mobile_phone")}
                        placeholder="Enter mobile phone"
                        value={formData.mobile_phone}
                        onChange={(e) =>
                          handleInputChange("mobile_phone", e.target.value)
                        }
                      />
                    </>
                  )}
                  {/* <TextInput
                    label="City"
                    placeholder="Enter city"
                    value={formData.city}
                    onChange={(e) => handleInputChange("city", e.target.value)}
                  />
                  <Select
                    label="State/Province"
                    placeholder={t("common:select_one")}
                    searchable
                    data={usStates.map((state) => ({
                      label: state.label,
                      value: state.value,
                    }))}
                    value={formData.state_province}
                    onChange={(e) => handleInputChange("state_province", e)}
                  /> */}
                </Stack>
              </>
            )}
            <Group>
              <Button fullWidth type="submit">
                Lookup
              </Button>
            </Group>
          </Stack>
        </form>
      </Card>

      {!identOnly && results && (
        <Card mt="md">
          <Flex gap="xs" align="center" mb="md">
            <Title order={4}>Results</Title>
            <ActionIcon
              radius="xl"
              variant="light"
              size="xs"
              color="gray"
              onClick={() => setResults(null)}
              title="Reset results"
            >
              <IconX size={12} />
            </ActionIcon>
          </Flex>
          {results ? (
            displayContacts?.map((result, index) => (
              <div key={index}>
                <ResultDisplay result={result} />
                {index < displayContacts.length - 1 && <Divider my="md" />}
              </div>
            ))
          ) : (
            <Text c="dimmed">No results found</Text>
          )}
          {displayContacts?.length > 0 && <Divider my="md" />}
          <Box>
            <RawResultCard result={results} />
          </Box>
        </Card>
      )}

      {identOnly && (
        <>
          {displayContacts?.length > 0 ? (
            <>
              <Stack gap="xs" mt="md">
                {displayContacts?.map((result, index) => (
                  <Card key={index}>
                    <IdentResultDisplay
                      result={result}
                      fetchData={() => mutation.mutate({})}
                    />
                  </Card>
                ))}
              </Stack>
            </>
          ) : (
            <>
              {showNoResultsMessage && (
                <Text mt="md" c="dimmed">
                  No results found
                </Text>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

const RawResultCard = ({ result }) => {
  return (
    <div>
      <Text fw={600} mb="xs">
        API Response
      </Text>
      <Code block>{JSON.stringify(result, null, 2)}</Code>
    </div>
  );
};

const ResultDisplay = ({ result }) => {
  const fieldsToDisplay = [
    { label: "HOG Member ID", value: result.hog_member_id || "N/A" },
    { label: "Membership Type", value: result.membership_type },
    { label: "Membership Status", value: result.membership_status },
    { label: "First Name", value: result.firstName || "N/A" },
    { label: "Last Name", value: result.lastName || "N/A" },
    {
      label: "Phone Number",
      value: result.mobilephone
        ? result.mobilephone
        : result.phone_number
        ? result.phone_number
        : "MISSING",
    },
    {
      label: "Membership Activated Date",
      value: result.activatedDate
        ? new Date(result.activatedDate).toLocaleDateString()
        : result.initial_activation_date
        ? new Date(result.initial_activation_date).toLocaleDateString()
        : "N/A",
    },
    {
      label: "Membership Expiration Date",
      value: result.membership_expiration_date
        ? new Date(result.membership_expiration_date).toLocaleDateString()
        : "N/A",
    },
  ];

  return (
    <Stack gap="xs">
      {fieldsToDisplay.map((field, index) => (
        <Group key={index} gap="xs">
          <Text fw={600}>{field.label}:</Text>
          <Text>{field.value}</Text>
        </Group>
      ))}
    </Stack>
  );
};

const IdentResultDisplay = ({ result, fetchData }) => {
  return (
    <Stack>
      <div>
        <Text fw={600}>
          {result.first_name} {result.last_name}
        </Text>
        <Text>{result.email}</Text>
        <Text>{result.phone || "Missing phone number"}</Text>
        <Text>
          {result.city}, {result.state} {result.country} {result.zip}
        </Text>
      </div>
      <Flex gap="xs">
        <Flex align="center" gap="xs">
          {result.is_active ? (
            <IconCircleCheck color="var(--mantine-color-green-6)" size={24} />
          ) : (
            <IconCircleX color="var(--mantine-color-red-6)" size={16} />
          )}
          <Text>{result.is_active ? "Active" : "Inactive"}</Text>
        </Flex>
        <Flex align="center" gap="xs">
          {result.confirmed_by_third_party_api ? (
            <IconCircleCheck color="var(--mantine-color-green-6)" size={24} />
          ) : (
            <IconCircleX color="var(--mantine-color-red-6)" size={16} />
          )}
          <Text>{result.is_active ? "Confirmed" : "Unconfirmed"}</Text>
        </Flex>
      </Flex>
      <div>
        {result.status === 1 && (
          <RemoveMembershipButton
            reqData={{
              loyalty_membership_id: result.loyalty_membership_id,
            }}
            fetchData={fetchData}
          />
        )}
        {result.status === 2 && <Text fw={600}>Membership removed</Text>}
      </div>
    </Stack>
  );
};

const RemoveMembershipButton = ({ reqData, fetchData }) => {
  const removeMembershipMutation = useMutation({
    mutationFn: async () => {
      return axios.post("/loyalty-memberships/remove-membership/", {
        ...reqData,
      });
    },
    onSuccess: () => {
      fetchData();
    },
    onError: (err) => {
      toast.error(err);
    },
  });

  return (
    <Button
      loading={removeMembershipMutation.isPending}
      variant="light"
      color="red"
      size="xs"
      onClick={() => removeMembershipMutation.mutate()}
      disabled={removeMembershipMutation.isPending}
    >
      Remove Membership
    </Button>
  );
};
